import { useComposerRootKeyDownInput } from '#pplx/frontend/composer-sdk/hooks/useComposerRootKeyDownInput.js';
import { useComposer } from '#pplx/frontend/composer-sdk/useComposer.js';
import { useCallback, useEffect, useRef } from 'react';
export function useComposerRootEventListeners({ editor, enterKeyBehavior = 'submit', onFocus, onBlur, onPaste, onBeforeInput, onClick, onEditableTouchStart, onKeyDown, }) {
    const { isComposing, setActive, setComposing } = useComposer();
    const onFocusRef = useRef(onFocus);
    const onBlurRef = useRef(onBlur);
    const onPasteRef = useRef(onPaste);
    const onBeforeInputRef = useRef(onBeforeInput);
    const onClickRef = useRef(onClick);
    const onEditableTouchStartRef = useRef(onEditableTouchStart);
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
    onPasteRef.current = onPaste;
    onBeforeInputRef.current = onBeforeInput;
    onClickRef.current = onClick;
    onEditableTouchStartRef.current = onEditableTouchStart;
    const hasEditableTouchStartHandler = onEditableTouchStart !== undefined;
    useComposerRootKeyDownInput({
        editor,
        enterKeyBehavior,
        isComposing,
        onCompositionStart: () => setComposing(true),
        onCompositionEnd: () => setComposing(false),
        onKeyDown,
    });
    const handleFocus = useCallback((event) => {
        onFocusRef.current?.(event);
        setActive(true);
    }, [setActive]);
    const handleBlur = useCallback((event) => {
        if (onBlurRef.current?.(event) !== false) {
            setActive(false);
        }
    }, [setActive]);
    const handlePaste = useCallback((event) => {
        onPasteRef.current?.(event);
    }, []);
    const handleBeforeInput = useCallback((event) => {
        onBeforeInputRef.current?.(event);
    }, []);
    const handleClick = useCallback((event) => {
        onClickRef.current?.(event);
    }, []);
    useEffect(() => {
        return editor?.registerRootListener((rootEl, prevEl) => {
            if (rootEl) {
                rootEl.addEventListener('focus', handleFocus);
                rootEl.addEventListener('blur', handleBlur);
                rootEl.addEventListener('beforeinput', handleBeforeInput);
                rootEl.addEventListener('paste', handlePaste);
                rootEl.addEventListener('click', handleClick);
            }
            if (prevEl) {
                prevEl.removeEventListener('focus', handleFocus);
                prevEl.removeEventListener('blur', handleBlur);
                prevEl.removeEventListener('beforeinput', handleBeforeInput);
                prevEl.removeEventListener('paste', handlePaste);
                prevEl.removeEventListener('click', handleClick);
            }
        });
    }, [editor, handleFocus, handleBlur, handleBeforeInput, handlePaste, handleClick]);
    useEffect(() => {
        if (!hasEditableTouchStartHandler) {
            return;
        }
        const handleTouchStart = (event) => {
            onEditableTouchStartRef.current?.(event);
        };
        return editor?.registerRootListener((rootEl, prevEl) => {
            prevEl?.removeEventListener('touchstart', handleTouchStart);
            // React's passive touch listeners cannot cancel native touch behavior.
            rootEl?.addEventListener('touchstart', handleTouchStart, { passive: false });
        });
    }, [editor, hasEditableTouchStartHandler]);
}
//# sourceMappingURL=useComposerRootEventListeners.js.map